import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { categories, categoryItems } from '@/mocks/categories';

export default function CategoryPage() {
  const [activeCategory, setActiveCategory] = useState(categories[0].id);

  const activeCat = categories.find((c) => c.id === activeCategory);
  const filteredItems = categoryItems.filter((item) => item.categoryId === activeCategory);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '影视分类浏览 - 91精品影视',
    description: '浏览91精品影视全部分类，热门推荐、最新上映、国产剧集、海外剧集、综艺娱乐、动漫番剧等海量影视资源。',
    url: 'https://91jingpinys.com/category',
  };

  return (
    <Layout>
      <SEO
        title="影视分类浏览"
        description="浏览91精品影视全部分类：热门推荐、最新上映、国产剧集、海外剧集、综艺娱乐、动漫番剧、纪录片、少儿频道。海量高清影视资源，总有一部适合你。"
        keywords="影视分类,电影分类,电视剧分类,综艺分类,动漫分类,91影视分类,热门推荐,最新上映"
        ogType="website"
        schemaObjects={[webPageSchema]}
      />

      <div className="pt-20 pb-4 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览' },
            ]}
          />
          <div className="px-4 md:px-8 pb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              影视分类浏览
            </h1>
            <p className="text-sm text-foreground-600">按分类探索你感兴趣的影视内容</p>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Category Content */}
      <section className="w-full py-10 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {activeCat && (
            <div className="mb-8 p-5 rounded-xl bg-accent-50 border border-accent-200/50">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className={`${activeCat.icon} text-xl`}></i>
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold text-foreground-900 mb-1">
                    {activeCat.name}
                  </h2>
                  <p className="text-sm text-foreground-600">{activeCat.description}</p>
                  <p className="text-xs text-accent-600 mt-2 font-medium">共 {activeCat.itemCount} 部作品</p>
                </div>
              </div>
            </div>
          )}

          {filteredItems.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className="group rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 overflow-hidden"
                >
                  <div className="aspect-[3/4] bg-background-200 relative overflow-hidden">
                    <div className="w-full h-full bg-background-300 flex items-center justify-center">
                      <i className="ri-film-line text-5xl text-foreground-400"></i>
                    </div>
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                      {item.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-full bg-primary-500 text-white text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-950/60 text-accent-400 text-xs font-semibold">
                      <i className="ri-star-fill text-xs"></i>
                      {item.rating}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-bold text-foreground-900 mb-1.5 line-clamp-1">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2 flex-wrap">
                      <span>{item.year}</span>
                      <span className="text-foreground-300">·</span>
                      <span>{item.type}</span>
                      <span className="text-foreground-300">·</span>
                      <span>{item.region}</span>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">该分类暂无内容</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}